import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Container,
    Grid,
    Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import axios from 'axios';
import React, { Fragment, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { IOrganizerEvent } from '../../../models/IEvent';
import EventServiceInstance from '../../../services/EventService';
import AuthContext from '../../../services/Auth/AuthContext';
import config from '../../../config';
import EventGeneralData, { IGeneralDataErrors } from './EventGeneralData';
import SaveIcon from '@mui/icons-material/Save';
import BackupIcon from '@mui/icons-material/Backup';
import PrintIcon from '@mui/icons-material/Print';
import PeopleIcon from '@mui/icons-material/People';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AlarmOnIcon from '@mui/icons-material/AlarmOn';
import DeleteIcon from '@mui/icons-material/Delete';
import StopIcon from '@mui/icons-material/Stop';
import RefreshIcon from '@mui/icons-material/Refresh';
import EventRounds from './EventRounds';
import EventGroups from './EventGroups';
import EventParticipants from './EventParticipants';
import IEditableRider from '../../../models/IEditableRider';
import { useSnackbar } from 'notistack';
import OntimeDropzoneDialog from '../../common/OntimeDropzoneDialog';
import OntimeProgress from '../../common/OntimeProgress';
import LoadingControl from '../../common/LoadingControl';
import { format } from 'util';
import PromptUnsavedChanges from '../../common/PromptUnsavedChanges';
import EventReaders from './EventReaders';
import ConfirmDialog from '../../common/ConfirmDialog';
import { defaultApiErrorDescription, getApiErrorDescription, getApiValidationErrors } from '../../../services/ServiceConstants';
import OntimeBreadcrumb from '../../common/OntimeBreadcrumb';
import RiderServiceInstance from '../../../services/RiderService';


const useStyles = makeStyles((theme) => ({
    table: {
        // minWidth: 650,
        tableLayout: 'auto'
    },
    container: {
        paddingTop: theme.spacing(10),
        paddingBottom: theme.spacing(4),
        paddingLeft: theme.spacing(2)
    },
    paper: {
        padding: theme.spacing(2),
        display: 'flex',
        overflow: 'auto',
        flexDirection: 'column',
    }
}));

const generalDataValidationFieldMap: Record<string, keyof IGeneralDataErrors> = {
    name: 'name',
    description: 'description',
    eventdate: 'eventDate',
    racetypeid: 'raceTypeId',
    registrationurl: 'registrationUrl',
    accesscode: 'accessCode'
};

const normalizeValidationFieldName = (fieldName: string): string =>
    fieldName.toLowerCase().replace(/[^a-z0-9]/g, '');

const mapValidationErrorsToGeneralData = (validationErrors: Record<string, string[]>): IGeneralDataErrors => {
    const mappedErrors: IGeneralDataErrors = {};
    Object.entries(validationErrors).forEach(([fieldName, messages]) => {
        if (messages.length === 0) {
            return;
        }

        const fieldCandidates = [
            normalizeValidationFieldName(fieldName),
            normalizeValidationFieldName(fieldName.split('.').pop() ?? fieldName)
        ];
        const mappedField = fieldCandidates
            .map(candidate => generalDataValidationFieldMap[candidate])
            .find(candidate => candidate !== undefined);

        if (mappedField && mappedErrors[mappedField] === undefined) {
            mappedErrors[mappedField] = messages[0];
        }
    });

    return mappedErrors;
};

function EventSetup() {
    const { eventId } = useParams();
    const classes = useStyles();
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const authService = useContext(AuthContext);

    const [evt, setEvent] = useState<IOrganizerEvent>();
    const [modified, setModified] = useState(false);
    const [files, setFiles] = useState<FileList>();
    const [openBackupFilesDropzone, setOpenBackupFilesDropzone] = useState(false);
    const [openParticipantsDropzone, setOpenParticipantsDropzone] = useState(false);
    const [progressMessage, setProgressMessage] = useState<string>();
    const [generalDataErrors, setGeneralDataErrors] = useState<IGeneralDataErrors>();
    const [openStopConfirmation, setOpenStopConfirmation] = useState(false);

    useEffect(() => {
        if (!eventId) return;
        const source = axios.CancelToken.source();
        EventServiceInstance.getEventOrganizer(eventId, source.token).then(e => {
            if (e) {
                if (!authService?.getIsOrganizer(e.organizerId)) {
                    navigate('/unauthorized');
                } else {
                    setEvent(e);
                }
            }
        }).catch((error) => {
            if (!axios.isCancel(error)) {
                console.error('Failed to fetch event organizer:', error);
            }
        });
        return () => {
            source.cancel();
        };
    }, [eventId, authService, navigate]);

    const generalDataChanged = (data: any) => {
        setEvent({ ...evt, ...data });
        setModified(true);
        setGeneralDataErrors(undefined);
    };

    const newParticipant = (participant: IEditableRider) => {
        if (evt) {
            if (!evt.upsertPilots) {
                evt.upsertPilots = [participant];
            } else {
                evt.upsertPilots.push(participant);
            }
        }
        setEvent(evt);
    }

    const updateParticipant = (participant: IEditableRider) => {
        updateParticipants([participant]);
    }

    const updateParticipants = (participants: IEditableRider[]) => {
        if (evt) {
            if (!evt.upsertPilots) {
                evt.upsertPilots = participants;
            } else {
                participants.forEach(participant => {
                    const i = evt.upsertPilots.findIndex(g => g.tagId === participant.tagId);
                    if (i === -1) {
                        // new participant
                        evt.upsertPilots.push(participant);
                    } else {
                        evt.upsertPilots[i] = participant;
                    }
                });

            }
            participants.forEach(participant => {
                const i = evt.pilots.findIndex(g => g.tagId === participant.tagId);
                if (i === -1) {
                    // new participant                
                    evt.pilots.push(participant);
                } else {
                    evt.pilots[i] = participant;
                }
            });
            setEvent(evt);
            setModified(true);
        }
    }

    const deleteParticipant = (selected: string[]) => {
        if (evt) {
            const resultingParticipants = evt.pilots.filter(rider => selected.indexOf(rider.tagId) === -1);
            evt.pilots = resultingParticipants;
            if (!evt.deletedPilots) {
                evt.deletedPilots = selected;
            } else {
                evt.deletedPilots.push(...selected);
            }
            if (evt.upsertPilots) {
                evt.upsertPilots = evt.upsertPilots.filter(rider => selected.indexOf(rider.tagId) === -1);
            }
            setEvent(evt);
            setModified(true);
        }
    }

    const saveChanges = async () => {
        if (evt) {
            setProgressMessage(t<string>("commonLabels.savingChanges"));
            try {
                const updatedEvent = await EventServiceInstance.saveEvent(evt);
                setEvent(updatedEvent);
                setModified(false);
                setGeneralDataErrors(undefined);
            } catch (error) {
                const validationErrors = getApiValidationErrors(error);
                if (validationErrors) {
                    const mappedErrors = mapValidationErrorsToGeneralData(validationErrors);
                    if (Object.keys(mappedErrors).length > 0) {
                        setGeneralDataErrors(mappedErrors);
                    } else {
                        setGeneralDataErrors(undefined);
                    }
                } else {
                    setGeneralDataErrors(undefined);
                }
                enqueueSnackbar(getApiErrorDescription(error), {
                    variant: 'error'
                });
            } finally {
                setProgressMessage(undefined);
            }
        }
    }
    const downloadEventSummary = async () => {
        if (evt) {
            setProgressMessage(t<string>("eventsetup.downloadEventSummaryInProgress"));
            try {
                await EventServiceInstance.downloadEventSummary(evt.raceId);
            } finally {
                setProgressMessage(undefined);
            }
        }
    }
    const exportParticipants = async () => {
        if (evt) {
            setProgressMessage(t<string>("eventsetup.exportParticipantsInProgress"));
            try {
                await EventServiceInstance.exportParticipants(evt.raceId);
            } finally {
                setProgressMessage(undefined);
            }
        }
    }

    const processBackupFiles = async () => {
        if (evt) {
            if (files) {
                const readerId = (evt.checkpoints.length === 1)
                    ? evt.checkpoints[0].readerId
                    : "";
                await EventServiceInstance.uploadBackupFiles(evt.raceId, readerId, files);
                enqueueSnackbar(t("eventsetup.filesSent"));
            } else {
                enqueueSnackbar(t("eventsetup.selectFiles"), {
                    variant: 'error'
                });
            }
        }
    }

    const handleProcessBackupFiles = (droppedFiles: any) => {
        setFiles(droppedFiles);
        processBackupFiles();
        setOpenBackupFilesDropzone(false);
    }

    const processParticipantFiles = async (participantFiles: File[]) => {
        if (evt) {
            if (participantFiles) {
                setProgressMessage(t<string>("eventsetup.importParticipantsInProgress"));
                try {
                    const parsingResults = await EventServiceInstance.importParticipants(evt.raceId, participantFiles);
                    const importedCount = parsingResults.importedCount;
                    const failedCount = parsingResults.failedCount;
                    const errorsFile = parsingResults.errorsFile;
                    setEvent(EventServiceInstance.merge(evt, parsingResults));
                    setModified(true);
                    let summary = format(
                        t<string>("eventsetup.importParticipantsResult"),
                        importedCount,
                        failedCount
                    );
                    let action: any;
                    if (errorsFile) {
                        summary = `${summary}\n${t("eventsetup.importParticipantsWithErrors")}`;
                        action = (<Button onClick={() => EventServiceInstance.downloadErrors(evt.raceId, errorsFile)}>{t("eventsetup.downloadErrors")}</Button>)
                    }
                    enqueueSnackbar(summary, {
                        action: action,
                        autoHideDuration: 30000,
                        variant: errorsFile ? 'error' : 'info'
                    });
                }
                catch (error) {
                    console.error(error);
                    enqueueSnackbar(t("eventsetup.importParticipantsError"), { variant: 'error' });
                } finally {
                    setProgressMessage(undefined);
                }
            } else {
                enqueueSnackbar(t("eventsetup.selectFiles"), {
                    variant: 'error'
                });
            }
        }
    }

    const startEvent = async () => {
        if (evt) {
            setProgressMessage(t<string>("commonLabels.loading"));
            try {
                const e = await EventServiceInstance.startEvent(evt.raceId);
                if (e) {
                    setEvent(e);
                }
            } catch (error) {
                console.error(error);
                showEventStatusError(error, "eventsetup.startError");
            } finally {
                setProgressMessage(undefined);
            }
        }
    }

    const stopEvent = async () => {
        if (evt) {
            setProgressMessage(t<string>("commonLabels.loading"));
            try {
                const e = await EventServiceInstance.stopEvent(evt.raceId);
                if (e) {
                    setEvent(e);
                }
            } catch (error) {
                console.error(error);
                showEventStatusError(error, "eventsetup.stopError");
            } finally {
                setProgressMessage(undefined);
            }
        }
    }

    const cancelEvent = async () => {
        if (evt) {
            setProgressMessage(t<string>("commonLabels.loading"));
            try {
                await EventServiceInstance.cancelEvent(evt.raceId);
                navigate('/events/organizer');
            } finally {
                setProgressMessage(undefined);
            }
        }
    }

    const recalculateEventLaps = async () => {
        if (evt) {
            setProgressMessage(t<string>("eventsetup.recalculateInProgress"));
            try {
                await EventServiceInstance.recalculateEventLaps(evt.raceId);
                enqueueSnackbar(t("eventsetup.recalculateSuccess"), { variant: 'success' });
            } catch (error) {
                enqueueSnackbar(t("eventsetup.recalculateError"), { variant: 'error' });
            } finally {
                setProgressMessage(undefined);
            }
        }
    }

    const recalculateRiderLaps = async (tagId: string) => {
        if (evt) {
            setProgressMessage(t<string>("eventsetup.recalculateInProgress"));
            try {
                await RiderServiceInstance.recalculateRiderLaps(evt.raceId, tagId);
                enqueueSnackbar(t("eventsetup.recalculateSuccess"), { variant: 'success' });
            } catch (error) {
                enqueueSnackbar(t("eventsetup.recalculateError"), { variant: 'error' });
            } finally {
                setProgressMessage(undefined);
            }
        }
    }

    const handleImportParticipants = async (droppedFiles: File[]) => {
        setOpenParticipantsDropzone(false);
        await processParticipantFiles(droppedFiles);
    }

    const showEventStatusError = (error: unknown, fallbackTranslationKey: string) => {
        const errorDescription = getApiErrorDescription(error);
        enqueueSnackbar(
            errorDescription === defaultApiErrorDescription
                ? t(fallbackTranslationKey)
                : errorDescription,
            { variant: 'error' }
        );
    };

    if (evt) {
        const setupNameMap: Record<string, string> = { [t('events.organizerEvents')]: '/events/organizer' };
        return (
            <Container>
                <PromptUnsavedChanges dirty={modified}></PromptUnsavedChanges>
                <ConfirmDialog
                    open={openStopConfirmation}
                    title={t("eventsetup.stopConfirmTitle")}
                    message={t("eventsetup.stopConfirmMessage")}
                    onAccept={() => { setOpenStopConfirmation(false); stopEvent(); }}
                    onCancel={() => setOpenStopConfirmation(false)}
                />
                <Grid container spacing={1} className={classes.container} >
                    <Grid item xs={12}>
                        <OntimeBreadcrumb nameMap={setupNameMap} current={evt.name} />
                    </Grid>
                    <Grid item xs={12}>
                        <OntimeProgress
                            open={progressMessage !== undefined}
                            message={progressMessage} />
                        {evt.status === 0 ?
                            (<Fragment>
                                <Button
                                    disabled={modified === true}
                                    variant="contained"
                                    color="primary"
                                    onClick={e => startEvent()}
                                    startIcon={<AlarmOnIcon />}
                                >{t("eventsetup.start")}</Button>
                                <Button
                                    disabled={modified === true}
                                    variant="contained"
                                    color="primary"
                                    onClick={e => cancelEvent()}
                                    startIcon={<DeleteIcon />}
                                >{t("eventsetup.delete")}</Button>
                            </Fragment>
                            ) : null}
                        {evt.status === 1 ? <Button
                            variant="contained"
                            color="primary"
                            onClick={e => setOpenStopConfirmation(true)}
                            startIcon={<StopIcon />}
                        >{t("eventsetup.stop")}</Button> : null}
                        <Button
                            disabled={modified === false}
                            variant="contained"
                            color="primary"
                            onClick={e => saveChanges()}
                            startIcon={<SaveIcon />}
                        >{t("commonActions.save")}</Button>
                        <Button
                            disabled={progressMessage !== undefined}
                            variant="contained"
                            color="primary"
                            onClick={e => downloadEventSummary()}
                            startIcon={<PrintIcon />}
                        >{t("events.downloadEventSummary")}</Button>
                        <Button
                            disabled={progressMessage !== undefined}
                            variant="contained"
                            color="primary"
                            onClick={e => exportParticipants()}
                            startIcon={<PeopleIcon />}
                        >{t("eventsetup.exportParticipants")}</Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={e => setOpenParticipantsDropzone(true)}
                            startIcon={<GroupAddIcon />}
                        >{t("eventsetup.importParticipants")}</Button>
                        

                        <OntimeDropzoneDialog
                            openDropzone={openBackupFilesDropzone}
                            acceptedFiles={['.csv', '.zip']}
                            dialogTitle={t("eventsetup.importBackupFiles")}
                            handleClose={e => setOpenBackupFilesDropzone(false)}
                            handleSave={handleProcessBackupFiles}
                        />
                        <OntimeDropzoneDialog
                            openDropzone={openParticipantsDropzone}
                            acceptedFiles={['.csv', '.xlsx']}
                            dialogTitle={t("eventsetup.importParticipants")}
                            handleClose={e => setOpenParticipantsDropzone(false)}
                            handleSave={handleImportParticipants}
                            filesLimit={1}
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={e => setOpenBackupFilesDropzone(true)}
                            startIcon={<BackupIcon />}
                        >{t("eventsetup.importBackupFiles")}</Button>
                        {authService?.isInRole(config.managementGroups.administrator) && (
                            <Button
                                disabled={progressMessage !== undefined}
                                variant="contained"
                                color="primary"
                                onClick={recalculateEventLaps}
                                startIcon={<RefreshIcon />}
                            >{t("eventsetup.recalculate")}</Button>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h5">{t("eventsetup.general")}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <EventGeneralData
                                    data={evt}
                                    errors={generalDataErrors}
                                    onDataChange={generalDataChanged}
                                />
                            </AccordionDetails>
                        </Accordion>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h5">{t("eventsetup.readers")}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <EventReaders
                                    data={evt}
                                    onDataChange={generalDataChanged}
                                />
                            </AccordionDetails>
                        </Accordion>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h5">{t("eventsetup.rounds")}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <EventRounds
                                    data={evt}
                                    eventDate={evt.eventDate}
                                    onDataChange={generalDataChanged}
                                />
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h5">{t("eventsetup.groups")}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <EventGroups
                                    data={evt}
                                    onDataChange={generalDataChanged}
                                    raceId={evt.raceId}
                                    riders={evt.pilots}
                                    onDistributeRiders={(updatedRiders, updatedGroups) => {
                                        if (evt) {
                                            const changedRiders = updatedRiders.filter(r => {
                                                const original = evt.pilots.find(p => p.tagId === r.tagId);
                                                return original && original.group !== r.group;
                                            });
                                            const newPilots = evt.pilots.map(p => {
                                                const updated = updatedRiders.find(r => r.tagId === p.tagId);
                                                return updated ?? p;
                                            });
                                            const newUpsertPilots = [...(evt.upsertPilots ?? [])];
                                            changedRiders.forEach(r => {
                                                const i = newUpsertPilots.findIndex(p => p.tagId === r.tagId);
                                                if (i === -1) {
                                                    newUpsertPilots.push(r);
                                                } else {
                                                    newUpsertPilots[i] = r;
                                                }
                                            });
                                            setEvent({
                                                ...evt,
                                                groups: updatedGroups,
                                                pilots: newPilots,
                                                upsertPilots: newUpsertPilots,
                                            });
                                            setModified(true);
                                        }
                                    }}
                                    onMoveRiders={(fromGroupId, toGroupId) => {
                                        const affected = evt.pilots.filter(p => p.group === fromGroupId);
                                        const updated = affected.map(p => ({ ...p, group: toGroupId }));
                                        updateParticipants(updated);
                                    }}
                                />
                            </AccordionDetails>
                        </Accordion>

                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography variant="h5">{t("eventsetup.participants")}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <EventParticipants
                                    groups={evt.groups}
                                    allActive={evt.raceMode}
                                    raceId={evt.raceId}
                                    isAdmin={authService?.isInRole(config.managementGroups.administrator)}
                                    data={evt}
                                    onDataChange={generalDataChanged}
                                    onNew={newParticipant}
                                    onUpdate={updateParticipant}
                                    onDelete={deleteParticipant}
                                    onRecalculateRider={authService?.isInRole(config.managementGroups.administrator) ? recalculateRiderLaps : undefined}
                                />
                            </AccordionDetails>
                        </Accordion>

                    </Grid>

                </Grid>
            </Container>
        );
    } else {
        return (<Container maxWidth="lg" className={classes.container}>
            <LoadingControl message={t("eventsetup.loading")} />
        </Container>);
    }

}

export default EventSetup;
